import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, ListTodo, FileCheck, UserPlus, UserCheck,
  RotateCcw, ArrowRight, X, Heart, Briefcase,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Voice Approval Panel — shows ALL side-effects of an Agent-Concierge voice
 * note before the advisor moves on. Inspired by Salesforce FSC "Einstein
 * Activity Capture" and Nitrogen "AI summary" approval UX.
 *
 * Backend has already persisted the contact / tasks / contract (one LLM call,
 * one DB write — cheaper than dry-run). This panel lets the advisor:
 *   • Skim what was created
 *   • Hit [SCHVÁLIT VŠE] → close + open the client card
 *   • Hit [Vrátit zpět] → /api/voice-notes/{id}/rollback (deletes tasks,
 *     auto-saved contract, fresh-created contact, and the note itself)
 *
 * Why post-hoc approval instead of pre-flight dry-run? The advisor records
 * 45-min meetings — re-running the LLM twice would burn $$ and add latency.
 */
export default function VoiceApprovalPanel({ result, onClose, onApprove }) {
  const [rolling, setRolling] = useState(false);

  const analysis = result?.analysis || {};
  const client = analysis.client || {};
  const contract = analysis.contract || {};
  const tasks = analysis.tasks || [];
  const summary = analysis.summary || "";
  const followUp = analysis.follow_up_message_draft || "";
  const matchInfo = result?.contact_match || {};
  const createdContact = matchInfo.action === "created_new";
  const linkedContact = matchInfo.action === "linked_existing" || matchInfo.action === "forced";

  const rollback = async () => {
    if (!window.confirm("Vrátit zpět všechny změny z této hlasovky? (smaže úkoly, smlouvu i případně vytvořený kontakt)")) return;
    setRolling(true);
    try {
      const { data } = await api.post(`/api/voice-notes/${result.id}/rollback`);
      const s = data?.summary || {};
      const bits = [];
      if (s.tasks_removed > 0) bits.push(`${s.tasks_removed} úkolů`);
      if (s.contract_removed) bits.push("smlouva");
      if (s.contact_removed) bits.push("kontakt");
      toast.success(bits.length ? `Vráceno zpět: ${bits.join(", ")}` : "Vráceno zpět");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback selhal");
    } finally {
      setRolling(false);
    }
  };

  const approve = async () => {
    try {
      const { data } = await api.post(`/api/voice-notes/${result.id}/approve`);
      // Big celebratory toast — "the Done moment"
      const stageLabel = data?.stage_label || "Lead";
      const clientName = data?.contact_name || client.name || "klient";
      const tasksTxt = data?.tasks_count > 0
        ? ` · ${data.tasks_count} ${data.tasks_count === 1 ? "úkol" : data.tasks_count < 5 ? "úkoly" : "úkolů"} v diáři`
        : "";
      const advancedTxt = data?.stage_advanced
        ? ` (posunuto z ${data.previous_stage?.toUpperCase() || "?"})`
        : "";
      toast.success(
        `Hotovo! ${clientName} je v ${stageLabel}${advancedTxt}${tasksTxt}.`,
        {
          duration: 6000,
          style: {
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            color: "#FFFFFF",
            border: "1px solid rgba(255,255,255,0.20)",
            fontSize: "14.5px",
            fontWeight: 600,
            padding: "14px 18px",
            boxShadow: "0 14px 36px rgba(16,185,129,0.38), 0 2px 0 rgba(255,255,255,0.18) inset",
          },
          className: "voice-approval-toast",
        }
      );
    } catch (e) {
      // Even if /approve fails, we still close — data is already saved on /record
      toast.success("Hotovo! Vše uloženo.");
    }
    onApprove?.(result?.contact_id);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[140] bg-black/75 backdrop-blur-md flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="voice-approval-panel"
    >
      <motion.div
        initial={{ y: 20, scale: 0.97, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl my-6 bg-gradient-to-br from-[#0E1B36] to-[#070D22] border border-[#D4AF37]/30 rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #D4AF37 50%, transparent)" }} />

        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1 flex items-center gap-2">
                <CheckCircle2 size={12} /> Agent Concierge · detekované akce
              </div>
              <h2 className="font-display text-2xl text-white" style={{ fontWeight: 600 }}>
                {client.name ? `Schůzka s ${client.name}` : "Hlasová poznámka"}
              </h2>
              {summary && (
                <p className="text-sm text-[#A0ABC0] mt-2 leading-relaxed max-w-prose">{summary}</p>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 text-[#A0ABC0] hover:text-white" aria-label="Zavřít">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Detection blocks */}
        <div className="px-6 py-5 space-y-4 max-h-[55vh] overflow-y-auto">

          {/* Client */}
          {(createdContact || linkedContact || client.name) && (
            <DetectionBlock
              icon={createdContact ? UserPlus : UserCheck}
              tone={createdContact ? "gold" : "emerald"}
              title={createdContact ? "Nový klient přidán" : "Klient propojen"}
              testid="approval-client-block"
            >
              <div className="text-sm text-white">{client.name || "—"}</div>
              <div className="text-[11px] text-[#A0ABC0] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {client.phone && <span>{client.phone}</span>}
                {client.email && <span>{client.email}</span>}
                {client.city && <span>{client.city}</span>}
              </div>
              {matchInfo.reason && (
                <div className="text-[10px] uppercase tracking-wider text-[#7A8AA8] mt-1.5">
                  {matchInfo.action === "created_new" ? "Vytvořeno nově" :
                   matchInfo.action === "forced" ? "Z karty klienta" :
                   `Spárováno: ${matchInfo.reason}${matchInfo.score ? ` (${matchInfo.score})` : ""}`}
                </div>
              )}
              {client.family_event && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#EC4899]/10 border border-[#EC4899]/30 text-[#F0A1C7] text-[10px]">
                  <Heart size={10} /> Životní událost: {client.family_event}
                </div>
              )}
            </DetectionBlock>
          )}

          {/* Tasks */}
          {tasks.length > 0 && (
            <DetectionBlock
              icon={ListTodo}
              tone="gold"
              title={`${tasks.length} úkolů v Diáři`}
              testid="approval-tasks-block"
            >
              <ul className="space-y-1.5 mt-1">
                {tasks.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_COLOR[t.priority] || "#D4AF37" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-white">{t.text || t.title}</div>
                      <div className="text-[10px] text-[#7A8AA8] mt-0.5 flex gap-2 flex-wrap">
                        {t.due && <span>{t.due}{t.time ? ` · ${t.time}` : ""}</span>}
                        {t.action_hint && <span className="uppercase tracking-wider">{t.action_hint}</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </DetectionBlock>
          )}

          {/* Contract */}
          {(result?.auto_contract_id || contract.type) && (
            <DetectionBlock
              icon={result?.auto_contract_id ? FileCheck : Briefcase}
              tone="emerald"
              title={result?.auto_contract_id ? "Smlouva uložena" : "Smlouva detekována"}
              testid="approval-contract-block"
            >
              <div className="text-sm text-white">
                {CONTRACT_TYPE_LABEL[contract.type] || contract.type || "—"}
                {contract.provider && <span className="text-[#D4AF37]"> · {contract.provider}</span>}
              </div>
              <div className="text-[11px] text-[#A0ABC0] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {contract.monthly_amount && <span>{Number(contract.monthly_amount).toLocaleString("cs-CZ")} Kč / měs</span>}
                {contract.total_amount && <span>Celkem {Number(contract.total_amount).toLocaleString("cs-CZ")} Kč</span>}
                {contract.rate && <span>{contract.rate} %</span>}
                {contract.fixation_end && <span>Fixace do {contract.fixation_end}</span>}
              </div>
              {!result?.auto_contract_id && (
                <div className="mt-1 text-[10px] text-[#F59E0B]">
                  Auto-uložení vynecháno (nedostatek údajů). Můžete přidat ručně ve složce klienta.
                </div>
              )}
            </DetectionBlock>
          )}

          {/* Follow-up draft */}
          {followUp && (
            <DetectionBlock icon={ArrowRight} tone="muted" title="Návrh follow-up zprávy" testid="approval-followup-block">
              <div className="text-sm text-[#C9D3E5] italic leading-relaxed">"{followUp}"</div>
              <button
                onClick={() => { navigator.clipboard.writeText(followUp); toast.success("Zkopírováno"); }}
                className="mt-2 text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline"
              >
                Zkopírovat do schránky
              </button>
            </DetectionBlock>
          )}

          {/* Nothing detected fallback */}
          {!createdContact && !linkedContact && !client.name && tasks.length === 0 && !contract.type && (
            <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4 flex items-start gap-3">
              <AlertCircle size={16} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#A0ABC0]">
                AI nedetekovala konkrétní akce. Přepis je uložen — můžete úkol přidat ručně v Diáři.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#070D22] flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <button
            onClick={rollback}
            disabled={rolling}
            data-testid="approval-rollback"
            className="px-4 py-2 rounded-lg text-sm text-[#A0ABC0] hover:text-white hover:bg-white/5 border border-white/10 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <RotateCcw size={13} /> {rolling ? "Vracím…" : "Vrátit zpět"}
          </button>
          <button
            onClick={approve}
            data-testid="approval-approve-all"
            className="px-6 py-2.5 rounded-lg text-[#06091F] font-semibold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            style={{
              background: "linear-gradient(135deg, #D4AF37 0%, #A88728 100%)",
              boxShadow: "0 10px 30px rgba(212,175,55,0.30)",
            }}
          >
            <CheckCircle2 size={15} /> Schválit vše
            {result?.contact_id && <ArrowRight size={14} />}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const PRIORITY_COLOR = { high: "#EF4444", medium: "#F59E0B", low: "#06B6D4" };
const CONTRACT_TYPE_LABEL = {
  hypoteka: "Hypotéka", stavebni: "Stavební spoření", uver: "Úvěr",
  investice: "Investice", penzijko: "Důchod",
  pojisteni_zivot: "Životní pojištění", pojisteni_auta: "Pojištění auta",
  pojisteni_majetek: "Pojištění majetku", zdravotni: "Zdravotní", jine: "Jiné",
  pojisteni_odpovednost: "Pojištění odpovědnosti", pojisteni_ostatni: "Ostatní pojištění",
};

function DetectionBlock({ icon: Icon, tone = "gold", title, children, testid }) {
  const palette = {
    gold:    { bg: "rgba(212,175,55,0.08)", border: "rgba(212,175,55,0.30)", fg: "#D4AF37" },
    emerald: { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.30)", fg: "#10B981" },
    muted:   { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.10)", fg: "#A0ABC0" },
  }[tone];
  return (
    <div
      data-testid={testid}
      className="rounded-xl p-4 border"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: palette.fg }} />
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: palette.fg }}>{title}</span>
      </div>
      {children}
    </div>
  );
}
